// @flow

import React from 'react';
import {Grid, Typography, makeStyles, Tooltip} from '@material-ui/core';
import {Warning} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';

import type {Conversation} from '../../../../../entities';
import {
  getConversationDisplayName,
  useCurrentConversation,
} from '../../../chat/utils/conversation';
import {getMultichatServiceIcon} from '../../../utils/service-icon';
import {timeAgo} from '../../../../../utils/date/timeAgo';
import {useCollaborators} from '../../../../configure/accounts/utils';
import {useFunnelConversationAction} from '../../utils';
// import type {StoreDevice} from '../../../../../entities';

type Props = {
  conversation: Conversation,
  isArchivedConversation: boolean,
};

const useStyles = makeStyles(theme => ({
  container: {
    borderRadius: 12,
    padding: '.5rem',
    backgroundColor: 'white',
    width: '100%',

    '&[data-unread="true"]': {
      backgroundColor: '#DEFAF3',
      border: '1px solid #B6ECDD',
    },

    '&[data-archived="true"]': {
      backgroundColor: '#F0F0F0',
      border: '1px solid #D3D3D3',
      color: '#A0A0A0',
    },
  },
  content: {
    width: '100%',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowEnd: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  details: {
    display: 'flex',
    gap: '.25rem',
    alignItems: 'center',
    width: '70%',
  },
  name: {
    fontWeight: 600,
    color: colors.grey900,
    fontSize: 14,
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  text: {
    fontSize: 12,
    fontWeight: 400,
    color: colors.grey700,
    whiteSpace: 'nowrap',
  },
  shortName: {
    width: '28px',
    height: '28px',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: colors.grey300,

    '& p': {
      fontWeight: 500,
      color: colors.grey900,
      fontSize: 10,
    },
  },
  conversationService: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  iconArchived: {
    color: '#fbaf19',
    fontSize: '1.3rem',
    textAlign: 'right',
    width: 24,
    height: 24,
  },
}));

const ConversationCard = (props: Props) => {
  const {conversation, isArchivedConversation} = props;

  const classes = useStyles();
  const {collaborators} = useCollaborators();

  const displayName = getConversationDisplayName(conversation);
  const shortName = displayName.substring(0, 2).toUpperCase();
  const serviceIcon = getMultichatServiceIcon(conversation.ravenService);

  const {setCurrentConversation} = useCurrentConversation();
  const {updateFunnelConversation} = useFunnelConversationAction();

  let assignedLabel = 'Sin asignar';

  if (conversation.userType === 'VENDOR') {
    assignedLabel = 'Administrador';
  }

  if (conversation.userId && conversation.userType !== 'VENDOR') {
    const assignedCollaborator = collaborators.find(
      collaborator => collaborator.id === conversation.userId
    );

    assignedLabel = assignedCollaborator?.firstName || 'Sin asignar';
  }

  const handleClick = () => {
    setCurrentConversation(conversation);
    if (conversation.read) return;
    updateFunnelConversation({
      ...conversation,
      read: true,
    });
  };

  return (
    <div
      className={classes.container}
      data-unread={!conversation.read}
      data-archived={isArchivedConversation}
      onClick={handleClick}
    >
      <div className={classes.content}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <div className={classes.row}>
              <div className={classes.details}>
                <div className={classes.shortName}>
                  <Typography>{shortName}</Typography>
                </div>
                <Typography className={classes.name}>{displayName}</Typography>
              </div>
              <Typography className={classes.text}>
                {timeAgo(conversation?.lastMessageAt, {
                  style: 'short',
                })}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.row}>
              <Typography className={classes.text}>{assignedLabel}</Typography>

              <div className={classes.row}>
                <picture className={classes.conversationService}>
                  <img src={serviceIcon} alt={conversation.ravenService} />
                </picture>
                {isArchivedConversation && (
                  <div className={classes.rowEnd}>
                    <Tooltip
                      placement="right"
                      enterTouchDelay={0}
                      title={'Archivado'}
                    >
                      <Warning className={classes.iconArchived} />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default ConversationCard;
